import React from "react";
import { ApolloError, useApolloClient, useQuery } from "@apollo/client";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import {
  GET_PAYEE_CURB_AREAS,
  GetPayeeCurbAreasData,
  GetPayeeCurbAreasVars,
  PayeeCurbArea,
} from "common/graphql/queries/GetPayeeCurbAreas";
import { GET_ZONE_NAME, GetZoneNameData, GetZoneNameVars } from "common/graphql/queries/GetZoneName";
import { buildAreaNamesByZoneName, isAreaFilterItem, isZoneFilterItem, toZoneNamesById } from "./filterHelpers";

const NO_CURB_AREAS: PayeeCurbArea[] = [];

interface UseZoneAreaLookupsOptions {
  payeeAccountId: string | undefined;
  /** Curb areas exist only for airports; other accounts skip that query. */
  isAirport: boolean;
  appliedFilters: GridFilterModel | undefined;
}

export interface ZoneAreaLookups {
  curbAreas: PayeeCurbArea[];
  /** The curb areas query's own error, for the Area filter's options. */
  curbAreasError: ApolloError | undefined;
  areaNamesByZoneName: Map<string, string[]>;
  zoneNamesById: Map<string, string>;
  /** An applied Zone or Area filter is still waiting on its names. */
  isLoading: boolean;
  /** A lookup an applied Zone or Area filter needs has failed. */
  error: ApolloError | undefined;
  /** Retries the lookup behind `error`. */
  retry: () => void;
  /** Loads the same lookups for Export, which can run before the queries above finish. */
  loadForExport: () => Promise<{ zoneNamesById: Map<string, string>; curbAreas: PayeeCurbArea[] }>;
}

/**
 * Loads the zone names and curb areas the notices table needs. Zone filters store zone ids and
 * notices record zone names on `street_address`, so both filters and the Area column go through
 * these lookups (AUT-6037).
 */
export const useZoneAreaLookups = ({
  payeeAccountId,
  isAirport,
  appliedFilters,
}: UseZoneAreaLookupsOptions): ZoneAreaLookups => {
  const client = useApolloClient();

  const zoneFilterIds = React.useMemo(() => {
    const ids = (appliedFilters?.items ?? []).filter(isZoneFilterItem).map((item) => item.value as string);
    return Array.from(new Set(ids));
  }, [appliedFilters]);

  const hasZoneFilter = zoneFilterIds.length > 0;
  const hasAreaFilter = (appliedFilters?.items ?? []).some(isAreaFilterItem);

  // Airports only. A client has only a handful of areas, so they load up front for the Area
  // filter's options and the Area column (AUT-5863).
  const {
    data: curbAreasData,
    loading: curbAreasLoading,
    error: curbAreasError,
    refetch: refetchCurbAreas,
  } = useQuery<GetPayeeCurbAreasData, GetPayeeCurbAreasVars>(GET_PAYEE_CURB_AREAS, {
    variables: { payeeAccountId: payeeAccountId ?? "" },
    skip: !isAirport || !payeeAccountId,
  });

  const {
    data: zoneNamesData,
    loading: zoneNamesLoading,
    error: zoneNamesError,
    refetch: refetchZoneNames,
  } = useQuery<GetZoneNameData, GetZoneNameVars>(GET_ZONE_NAME, {
    variables: { zoneIds: zoneFilterIds },
    skip: !hasZoneFilter,
  });

  const curbAreas = curbAreasData?.curbAreas ?? NO_CURB_AREAS;

  const areaNamesByZoneName = React.useMemo(() => buildAreaNamesByZoneName(curbAreas), [curbAreas]);

  const zoneNamesById = React.useMemo(() => toZoneNamesById(zoneNamesData?.zones ?? []), [zoneNamesData]);

  const failedZoneNames = hasZoneFilter ? zoneNamesError : undefined;
  const failedCurbAreas = hasAreaFilter ? curbAreasError : undefined;

  const retry = () => {
    if (failedZoneNames) {
      refetchZoneNames();
    } else if (failedCurbAreas) {
      refetchCurbAreas();
    }
  };

  // Reads from the cache once the queries above have loaded.
  const loadForExport = async () => {
    const [zoneNamesResult, curbAreasResult] = await Promise.all([
      hasZoneFilter
        ? client.query<GetZoneNameData, GetZoneNameVars>({
            query: GET_ZONE_NAME,
            variables: { zoneIds: zoneFilterIds },
          })
        : undefined,
      isAirport && payeeAccountId
        ? client.query<GetPayeeCurbAreasData, GetPayeeCurbAreasVars>({
            query: GET_PAYEE_CURB_AREAS,
            variables: { payeeAccountId },
          })
        : undefined,
    ]);

    return {
      zoneNamesById: toZoneNamesById(zoneNamesResult?.data.zones ?? []),
      curbAreas: curbAreasResult?.data.curbAreas ?? NO_CURB_AREAS,
    };
  };

  return {
    curbAreas,
    curbAreasError,
    areaNamesByZoneName,
    zoneNamesById,
    isLoading: (hasZoneFilter && zoneNamesLoading) || (hasAreaFilter && curbAreasLoading),
    error: failedZoneNames ?? failedCurbAreas,
    retry,
    loadForExport,
  };
};
